import { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { Header } from "../../../common/Header/Header";
import { Modal } from "react-bootstrap";
import { ActionButton, AltButton } from "../../../common/Buttons/Buttons";
import {
  FilterDatePicker,
  SearchInput,
  SelectPerPage,
} from "../../../common/Forms/Input/Input";
import { PaymentsValidationTable } from "../../../common/Tables/Tables";
import { LoadSpinner } from "../../../common/LoadSpinner/LoadSpinner";
import { servicesService } from "../../../../services/servicesService";
import { notify } from "../../../../assets/helpers/toast";
import { paymentsValidationHeader } from "../../../../assets/helpers/tables";

import "../../Stats/Exportables/Exportables.scss";
import "./PaymentsValidation.scss";

const itemsPerpage = [10, 25, 50, 100, 500, 1000, 2500, 5000];

export const PaymentsValidation = () => {
  const [sinceDate, setSinceDate] = useState(new Date());
  const [untilDate, setUntilDate] = useState(new Date());
  const [paymentsValidationData, setPaymentsValidationData] = useState({
    limit: itemsPerpage[2],
    offset: 0,
    data: [],
    count: 0,
    page: 1,
    isLoading: true,
    search: "",
  });
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const selectitemsPerpage = (event) => {
    setPaymentsValidationData((prevState) => {
      return {
        ...prevState,
        page: 1,
        offset: 0,
        limit: parseInt(event.target.value),
      };
    });
  };

  const handlePageChange = (event, value) => {
    setPaymentsValidationData((prevState) => {
      return {
        ...prevState,
        page: value,
        offset: (value - 1) * paymentsValidationData?.limit,
      };
    });
  };

  const handleSinceDateChange = (date) => {
    if (date > untilDate) {
      notify("Ingrese una fecha en un rango valido", "info");
      return;
    }

    setSinceDate(date);
    setPaymentsValidationData((prevState) => ({
      ...prevState,
      page: 1,
      offset: 0,
    }));
  };

  const handleUntilDateChange = (date) => {
    if (date < sinceDate) {
      notify("Ingrese una fecha en un rango valido", "info");
      return;
    }

    setUntilDate(date);
    setPaymentsValidationData((prevState) => ({
      ...prevState,
      page: 1,
      offset: 0,
    }));
  };

  const handleSearchChange = (e) => {
    const { value } = e?.target;
    setPaymentsValidationData((prevState) => ({
      ...prevState,
      search: value,
      page: 1,
      offset: 0,
    }));
  };

  const parseRows = (responseData) => {
    if (Array.isArray(responseData?.data?.payments)) {
      return responseData?.data?.payments;
    }
    if (Array.isArray(responseData?.data?.results)) {
      return responseData?.data?.results;
    }
    if (Array.isArray(responseData?.data?.docs)) {
      return responseData?.data?.docs;
    }
    if (Array.isArray(responseData)) {
      return responseData;
    }
    if (Array.isArray(responseData?.results)) {
      return responseData?.results;
    }
    if (Array.isArray(responseData?.data)) {
      return responseData?.data;
    }
    if (Array.isArray(responseData?.payments)) {
      return responseData?.payments;
    }
    if (Array.isArray(responseData?.docs)) {
      return responseData?.docs;
    }
    return [];
  };

  const parseCount = (responseData, rows) => {
    return (
      responseData?.total ||
      responseData?.data?.total ||
      responseData?.data?.count ||
      responseData?.data?.totalDocs ||
      responseData?.count ||
      responseData?.totalDocs ||
      rows?.length ||
      0
    );
  };

  const getPaymentsValidation = useCallback(
    (byMail = false) => {
      setPaymentsValidationData((prevState) => ({
        ...prevState,
        isLoading: true,
      }));

      let params = {
        dateFrom: moment(sinceDate).format("YYYY/MM/DD"),
        dateTo: moment(untilDate).format("YYYY/MM/DD"),
        limit: paymentsValidationData?.limit,
        offset: paymentsValidationData?.offset,
        serviceCode: paymentsValidationData?.search,
      };

      servicesService
        .getPaymentsValidation(params)
        .then((response) => {
          if (response?.status === 200) {
            const rows = parseRows(response?.data);
            const total = response?.data?.total || parseCount(response?.data, rows);
            setPaymentsValidationData((prevState) => ({
              ...prevState,
              data: rows,
              count: total,
              isLoading: false,
            }));
            return;
          }

          notify(
            response?.message || "Error al consultar validación de pagos",
            "error"
          );
          setPaymentsValidationData((prevState) => ({
            ...prevState,
            isLoading: false,
          }));
        })
        .catch(() => {
          notify("Error al consultar validación de pagos", "error");
          setPaymentsValidationData((prevState) => ({
            ...prevState,
            isLoading: false,
          }));
        });
    },
    [
      paymentsValidationData?.limit,
      paymentsValidationData?.offset,
      paymentsValidationData?.search,
      sinceDate,
      untilDate,
    ]
  );

  useEffect(() => {
    getPaymentsValidation(false);
  }, [getPaymentsValidation]);

  const handleValidatePayment = (payment) => {
    setSelectedPayment(payment);
    setShowValidateModal(true);
  };

  const closeValidateModal = () => {
    setShowValidateModal(false);
    setSelectedPayment(null);
  };

  const confirmValidatePayment = () => {
    const payment = selectedPayment;
    if (!payment?._id) {
      notify("No se pudo identificar el pago a validar", "error");
      closeValidateModal();
      return;
    }

    setShowValidateModal(false);
    setPaymentsValidationData((prevState) => ({
      ...prevState,
      isLoading: true,
    }));

    servicesService
      .validatePayment({
        id: payment?._id,
      })
      .then((response) => {
        if (response?.status === 200) {
          notify("Pago validado correctamente", "success");
          setSelectedPayment(null);
          getPaymentsValidation(false);
          return;
        }

        notify(response?.message || "Error al validar pago", "error");
        setPaymentsValidationData((prevState) => ({
          ...prevState,
          isLoading: false,
        }));
        setSelectedPayment(null);
      })
      .catch(() => {
        notify("Error al validar pago", "error");
        setPaymentsValidationData((prevState) => ({
          ...prevState,
          isLoading: false,
        }));
        setSelectedPayment(null);
      });
  };

  return (
    <>
      <LoadSpinner show={paymentsValidationData?.isLoading} />
      <Modal
        centered
        show={showValidateModal}
        onHide={closeValidateModal}
      >
        <Modal.Header closeButton className="modal-header"></Modal.Header>
        <Modal.Body className="modal-body">
          <h4 style={{ marginBottom: "12px" }}>Confirmar validación</h4>
          <p style={{ marginBottom: "18px", textAlign: "center" }}>
            ¿Seguro que deseas validar el pago del servicio <br />
            <b>{selectedPayment?.service?.code || selectedPayment?.serviceCode || "N/A"}</b>?
          </p>
          <div className="buttons-center-container">
            <AltButton action={closeValidateModal} label={"Cancelar"} />
            <ActionButton action={confirmValidatePayment} label={"Validar pago"} />
          </div>
        </Modal.Body>
        <Modal.Footer className="modal-footer"></Modal.Footer>
      </Modal>
      <Header />
      <div className="service-with-container container">
        <div className="buttons-container">
          <div>
            <h1 className="title-component">
              Validación de pagos ({paymentsValidationData?.count || 0})
            </h1>
            <h6 className="payments-validation-breadcrumb">
              TU/GRUERO / ANALÍTICAS / <b>CASHEA</b>
            </h6>
          </div>
          <ActionButton
            label={"Exportar"}
            action={() => getPaymentsValidation(true)}
          />
        </div>

        <div className="breadcrumb-container">
          <span></span>
          <div style={{ width: "auto" }} className="exportables-buttons-container">
            <FilterDatePicker
              bootstrap={"col-4"}
              label={"Desde"}
              type={"date"}
              name={"sinceDate"}
              placeholder={""}
              onChange={handleSinceDateChange}
              value={sinceDate}
            />
            <FilterDatePicker
              bootstrap={"col-4"}
              label={"Hasta"}
              type={"date"}
              name={"untilDate"}
              placeholder={""}
              onChange={handleUntilDateChange}
              value={untilDate}
            />
            <div style={{ marginTop: "12px" }}>
              <SelectPerPage
                label={"Registros"}
                options={itemsPerpage}
                value={paymentsValidationData?.limit}
                onChange={selectitemsPerpage}
              />
            </div>
          </div>
        </div>

        <div style={{ marginRight: "12px" }}>
          <SearchInput
            placeholder={"Código de servicio"}
            width="260px"
            value={paymentsValidationData?.search}
            onChange={handleSearchChange}
            searchAction={() => getPaymentsValidation(false)}
          />
        </div>

        {!paymentsValidationData?.isLoading && (
          <PaymentsValidationTable
            header={paymentsValidationHeader}
            handlePageChange={handlePageChange}
            data={paymentsValidationData}
            onValidate={handleValidatePayment}
          />
        )}
      </div>
    </>
  );
};
